import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormHelperText,
  LinearProgress,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Home as HomeIcon,
  Assessment as AssessmentIcon,
  CloudUpload as CloudUploadIcon,
  TableChart as TableChartIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';

interface Construction {
  constructionId: number;
  name: string;
  fileName: string;
  fileSize: number;
  worksheetCount: number;
  status: string;
  createdAt: string;
  worksheets?: WorksheetInfo[];
}

interface WorksheetInfo {
  name: string;
  index: number;
  visible: boolean;
}

export const XlsxConstructionPage: React.FC = () => {
  const navigate = useNavigate();
  const { appraisalId } = useParams<{ appraisalId: string }>();
  const { authenticatedFetch } = useAuthenticatedFetch();
  
  const [constructions, setConstructions] = useState<Construction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Upload form state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [constructionName, setConstructionName] = useState('');
  const [nameError, setNameError] = useState('');
  const [fileError, setFileError] = useState('');

  // Fetch constructions for this appraisal
  const fetchConstructions = useCallback(async () => {
    try {
      setError(null);
      // Note: This endpoint would need to be created to list constructions for an appraisal
      const response = await authenticatedFetch(`/api/appraisals/admin/${appraisalId}/constructions`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        setConstructions(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch constructions');
      }
    } catch (err) {
      console.error('Error fetching constructions:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch constructions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [authenticatedFetch, appraisalId]);

  useEffect(() => {
    if (appraisalId) {
      fetchConstructions();
    } else {
      setLoading(false);
      setError('Appraisal ID is required');
    }
  }, [fetchConstructions, appraisalId]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchConstructions();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setFileError('');
    
    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setFileError('Please select an Excel workbook file (.xlsx)');
      setSelectedFile(null);
      return;
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setFileError('File size must be less than 10MB');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value;
    setConstructionName(name);
    setNameError('');
    
    if (name.length > 255) {
      setNameError('Construction name must be less than 255 characters');
    }
  };

  const handleUpload = async () => {
    // Validate form
    let hasErrors = false;
    
    if (!constructionName.trim()) {
      setNameError('Construction name is required');
      hasErrors = true;
    }
    
    if (!selectedFile) {
      setFileError('Please select an Excel file');
      hasErrors = true;
    }
    
    if (hasErrors) return;

    setUploading(true);
    setUploadProgress(10);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile!);
      formData.append('name', constructionName.trim());
      formData.append('appraisalId', appraisalId!);

      setUploadProgress(30);

      const response = await authenticatedFetch(`/api/appraisals/construction/xlsx/create`, {
        method: 'POST',
        body: formData,
      });

      setUploadProgress(70);

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setUploadProgress(100);
        
        // Reset form and close dialog
        setConstructionName('');
        setSelectedFile(null);
        setUploadDialogOpen(false);
        
        // Refresh the list
        fetchConstructions();
        
        // Navigate to the document identification wizard
        navigate(`/appraisals/construction/xlsx/${result.data.constructionId}/identify`);
      } else {
        throw new Error(result.message || 'Failed to upload Excel workbook');
      }
    } catch (err) {
      console.error('Error uploading Excel workbook:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload Excel workbook');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleViewWorksheets = async (constructionId: number) => {
    navigate(`/appraisals/construction/xlsx/${constructionId}/sheets`);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'uploaded': return 'success';
      case 'processing': return 'warning';
      case 'error': return 'error';
      case 'identified': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
        <Link 
          color="inherit" 
          href="/dashboard"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <HomeIcon fontSize="inherit" />
          Dashboard
        </Link>
        <Link 
          color="inherit" 
          href="/appraisals"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <AssessmentIcon fontSize="inherit" />
          Appraisals
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TableChartIcon fontSize="inherit" />
          Excel Construction Analysis
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Excel Construction Analysis
        </Typography>
        <Box>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={refreshing}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
            sx={{ ml: 1 }}
          >
            Upload Excel Workbook
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Constructions Grid */}
      {constructions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CloudUploadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Excel workbooks uploaded yet
          </Typography>
          <Typography color="text.secondary" paragraph>
            Upload an Excel workbook to begin construction analysis and data extraction.
          </Typography>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload Excel Workbook
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {constructions.map((construction) => (
            <Grid item xs={12} md={6} lg={4} key={construction.constructionId}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom noWrap>
                    {construction.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom noWrap>
                    {construction.fileName}
                  </Typography>
                  
                  <Box mt={2} mb={2}>
                    <Chip 
                      label={construction.status} 
                      color={getStatusColor(construction.status)}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      File Size: {formatFileSize(construction.fileSize)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Worksheets: {construction.worksheetCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Uploaded: {new Date(construction.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </CardContent>
                
                <CardActions>
                  <Tooltip title="View Worksheets">
                    <IconButton 
                      size="small" 
                      onClick={() => handleViewWorksheets(construction.constructionId)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Button 
                    size="small" 
                    onClick={() => navigate(`/appraisals/construction/xlsx/${construction.constructionId}/identify`)}
                  >
                    Identify Documents
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Upload Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={() => !uploading && setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Excel Workbook</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <TextField
              fullWidth
              label="Construction Name"
              value={constructionName}
              onChange={handleNameChange}
              error={!!nameError}
              helperText={nameError}
              margin="normal"
              disabled={uploading}
            />
            
            <Box mt={2} mb={2}>
              <input
                type="file"
                accept=".xlsx"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="excel-file-input"
                disabled={uploading}
              />
              <label htmlFor="excel-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                  disabled={uploading}
                >
                  {selectedFile ? selectedFile.name : 'Select Excel File (.xlsx)'}
                </Button>
              </label>
              {fileError && (
                <FormHelperText error>{fileError}</FormHelperText>
              )}
            </Box>

            {selectedFile && (
              <Alert severity="info">
                File: {selectedFile.name} ({formatFileSize(selectedFile.size)})
              </Alert>
            )}

            {uploading && (
              <Box mt={2}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="body2" color="text.secondary" align="center" mt={1}>
                  Uploading... {uploadProgress}%
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setUploadDialogOpen(false)} 
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpload} 
            variant="contained"
            disabled={uploading || !selectedFile || !constructionName.trim()}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};